import { createPart, themeQuartz } from 'ag-grid-enterprise'
import { css, styled } from 'styled-components'

import { Button } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, pxToRem } from '@b-stock/fe-core/design-system'

import { cellStyles } from './columns/cellStyles'

const HEADER_HEIGHT = 42
export const ROW_HEIGHT = 52
export const SCROLLBAR_WIDTH = 10
// Floor for the row-count-aware bounded height below, so the container has a
// reasonable minimum size before `total` is known (e.g. before the first
// fetch resolves) rather than collapsing to just the header.
const MIN_VISIBLE_ROWS = 5
// Upper bounds for the manifest grid's bounded (infinite-scroll) height.
const MANIFEST_BOUNDED_HEIGHT_DESKTOP = 'max(52rem, 69vh)'
const MANIFEST_BOUNDED_HEIGHT_MOBILE = 'max(23.5rem, 53vh)'

export const fontFamilyInter =
  'Inter, -apple-system, BlinkMacSystemFont, sans-serif'

const tableStyles = `
  .ag-root-wrapper {
    border: none;
  }

  .ag-header-cell-label {
    color: ${Colors.Semantic.Neutral.dark_grey};
    font-size: ${pxToRem(10.5)};
    font-weight: 800;
    letter-spacing: 0.025rem;
    text-transform: uppercase;

    .ag-header-cell-sorted-desc &,
    .ag-header-cell-sorted-asc & {
      color: ${Colors.Semantic.BStock.default.lightBackground};
    }
  }

  .ag-sort-none-icon {
    color: ${Colors.Semantic.Neutral.medium_grey};
  }

  .ag-overlay {
    pointer-events: auto;
  }

  .ag-body-horizontal-scroll-viewport {
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: ${SCROLLBAR_WIDTH}px;
    }
  }
`

export const gridTheme = themeQuartz
  .withParams({
    borderColor: Colors.Semantic.Neutral.medium_grey,
    headerRowBorder: `0.125rem solid ${Colors.Semantic.Neutral.medium_grey}`,
    borderRadius: 0,
    cellFontSize: pxToRem(13),
    cellTextColor: '#4B5563',
    // This non-standard font stack is (critically) more legible at small sizes.
    fontFamily: fontFamilyInter,
    headerBackgroundColor: Colors.Semantic.Neutral.light_grey,
    headerHeight: HEADER_HEIGHT,
    rowHeight: ROW_HEIGHT,
    textColor: Colors.Semantic.Neutral.black,
    wrapperBorderRadius: 0,
  })
  .withPart(createPart({ css: cellStyles }))
  .withPart(createPart({ css: tableStyles }))

export const Header = styled.header`
  align-items: center;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
`

export const ManifestCardTitle = styled.h2`
  color: black;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.375rem;
  letter-spacing: 0.09rem;
  margin: 0;
  text-transform: uppercase;
`

export const GridContainer = styled.div<{
  $boundedInlineScroll?: boolean
  $fillHeight?: boolean
}>`
  width: 100%;

  --ag-header-column-resize-handle-color: transparent;

  /* [BP-2376] $fillHeight (fullscreen) and $boundedInlineScroll (inline +
   * infinite scroll) both exist to give domLayout="normal" a real resolved
   * height to establish AG Grid's internal scroll viewport — they're mutually
   * exclusive (see ManifestGrid's isFullscreen/isInlineInfiniteScroll) but use
   * different mechanisms because their ancestors differ: fullscreen already
   * sits inside a 100vh modal and can just delegate via flex, while inline has
   * no such ancestor and needs an explicit height of its own.
   */
  ${(o) =>
    o.$fillHeight &&
    css`
      flex: 1;
      min-height: 0;
    `}

  ${(o) =>
    o.$boundedInlineScroll &&
    css`
      /* Sized to content (header + row count) up to MANIFEST_BOUNDED_HEIGHT_*,
       * via a --manifest-row-count custom property set by the caller — this
       * avoids a mostly-blank box for small manifests while still
       * capping/scrolling large ones.
       */
      height: min(
        ${MANIFEST_BOUNDED_HEIGHT_DESKTOP},
        calc(
          ${HEADER_HEIGHT}px +
            max(
              ${MIN_VISIBLE_ROWS},
              var(--manifest-row-count, ${MIN_VISIBLE_ROWS})
            ) *
            ${ROW_HEIGHT}px
        )
      );

      @media ${Breakpoints.max.medium} {
        height: min(
          ${MANIFEST_BOUNDED_HEIGHT_MOBILE},
          calc(
            ${HEADER_HEIGHT}px +
              max(
                ${MIN_VISIBLE_ROWS},
                var(--manifest-row-count, ${MIN_VISIBLE_ROWS})
              ) *
              ${ROW_HEIGHT}px
          )
        );
      }
    `}
`

export const controlColors = css`
  background-color: ${Colors.Semantic.Neutral.white};
  border-color: ${Colors.Semantic.Neutral.very_dark_grey};
  color: ${Colors.Semantic.Neutral.very_dark_grey};

  &:hover {
    background-color: ${Colors.Semantic.Neutral.light_grey};
    border-color: ${Colors.Semantic.Neutral.very_dark_grey};
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  }
`

export const ControlButton = styled(Button)<{ $disabled?: boolean }>`
  ${(o) => o.appearance === 'secondary' && controlColors}
  opacity: ${(o) => (o.$disabled ? '0.6' : 1)};
`

export const ControlButtonLabel = styled.span`
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

// Fades to white at bottom but leaves the bottom border
export const FadeOutMask = styled.div`
  position: relative;

  &::after {
    background: linear-gradient(to bottom, transparent 60%, white);
    bottom: 0.0625rem;
    content: '';
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }
`

export const Footer = styled.footer`
  align-items: center;
  background-color: ${Colors.Semantic.Neutral.light_grey};
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  padding: ${pxToRem(11)} 1rem;
`
